/**
 * SpiderManager - patrol motion for all spiders on the playfield.
 *
 * Each spider has a static patrol segment (from the generator) and ticks one
 * tile along it on a shared clock, ping-ponging at the endpoints. Each spider
 * starts at its own initial offset so they're out of phase.
 *
 * Logical position updates instantly on tick. The visual tweens to catch up
 * over MOVE_TWEEN_MS. The chimp's collision check uses logical position.
 *
 * Tick interval is currently fixed; Adventure mode (step 7) will modulate it
 * per level. To support that, the interval is settable via setTickInterval().
 */

import Phaser from 'phaser';
import { TILE_SIZE } from '../config';
import type { Spider } from '../maze/types';
import { tickPatrol } from './patrol';
import { AssetManager } from '../assets/AssetManager';

const DEFAULT_TICK_INTERVAL_MS = 800;
const MOVE_TWEEN_MS = 150;

interface SpiderInstance {
  def: Spider;
  /** Current offset along the segment, 0..def.length-1. */
  offset: number;
  /** Travel direction along the segment: +1 forward, -1 backward. */
  dir: 1 | -1;
  container: Phaser.GameObjects.Container;
  /** Sprite child (if assets loaded). Used for flip + animation control. */
  sprite?: Phaser.GameObjects.Sprite;
  tween?: Phaser.Tweens.Tween;
}

export class SpiderManager {
  private scene: Phaser.Scene;
  private spiders: SpiderInstance[] = [];
  private elapsed = 0;
  private tickIntervalMs = DEFAULT_TICK_INTERVAL_MS;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  setTickInterval(ms: number): void {
    this.tickIntervalMs = ms;
  }

  /**
   * Build (or rebuild) spider instances from a fresh set of definitions.
   * Tears down any existing visuals and tweens first.
   */
  rebuild(defs: Spider[]): void {
    this.teardown();
    this.elapsed = 0;
    this.spiders = defs.map((def) => {
      const visual = this.makeVisual();
      const inst: SpiderInstance = {
        def,
        offset: def.initialOffset,
        dir: 1,
        container: visual.container,
        sprite: visual.sprite,
      };
      this.snapTo(inst);
      this.applyFacing(inst);
      // Start the walk anim if this spider has one. Spiders walk continuously,
      // so no need for the linger-and-pause pattern the chimp uses.
      if (inst.sprite && AssetManager.walkAnimKey('spider')) {
        inst.sprite.play(AssetManager.walkAnimKey('spider')!);
      }
      return inst;
    });
  }

  /** Reset offsets to initial and snap visuals back (same defs, no rebuild). */
  reset(): void {
    this.elapsed = 0;
    for (const s of this.spiders) {
      if (s.tween && s.tween.isPlaying()) s.tween.stop();
      s.offset = s.def.initialOffset;
      s.dir = 1;
      this.snapTo(s);
      this.applyFacing(s);
    }
  }

  /**
   * Advance the clock. Returns true if a tick happened this frame. The caller
   * should run collision checks against the chimp's tile after a true return.
   */
  update(dt: number): boolean {
    this.elapsed += dt;
    if (this.elapsed < this.tickIntervalMs) return false;
    this.elapsed -= this.tickIntervalMs;

    for (const s of this.spiders) {
      const next = tickPatrol(s.offset, s.dir, s.def.length);
      s.offset = next.offset;
      s.dir = next.dir;
      this.tweenTo(s);
      this.applyFacing(s);
    }
    return true;
  }

  /** True if any spider's logical tile is (x, y). */
  isAt(x: number, y: number): boolean {
    for (const s of this.spiders) {
      const p = this.tileOf(s);
      if (p.x === x && p.y === y) return true;
    }
    return false;
  }

  destroy(): void {
    this.teardown();
  }

  // --- Internals --------------------------------------------------------

  private tileOf(s: SpiderInstance): { x: number; y: number } {
    if (s.def.axis === 'horizontal') {
      return { x: s.def.start.x + s.offset, y: s.def.start.y };
    }
    return { x: s.def.start.x, y: s.def.start.y + s.offset };
  }

  private snapTo(s: SpiderInstance): void {
    const p = this.tileOf(s);
    s.container.setPosition(
      p.x * TILE_SIZE + TILE_SIZE / 2,
      p.y * TILE_SIZE + TILE_SIZE / 2
    );
  }

  private tweenTo(s: SpiderInstance): void {
    const p = this.tileOf(s);
    const tx = p.x * TILE_SIZE + TILE_SIZE / 2;
    const ty = p.y * TILE_SIZE + TILE_SIZE / 2;
    if (s.tween && s.tween.isPlaying()) s.tween.stop();
    s.tween = this.scene.tweens.add({
      targets: s.container,
      x: tx,
      y: ty,
      duration: MOVE_TWEEN_MS,
      ease: 'Sine.easeOut',
    });
  }

  private makeVisual(): { container: Phaser.GameObjects.Container; sprite?: Phaser.GameObjects.Sprite } {
    let child: Phaser.GameObjects.GameObject;
    let sprite: Phaser.GameObjects.Sprite | undefined;
    if (AssetManager.has('spider')) {
      sprite = this.scene.add.sprite(0, 0, 'spider');
      const frameH = sprite.frame.height;
      if (frameH !== TILE_SIZE) sprite.setScale(TILE_SIZE / frameH);
      child = sprite;
    } else {
      const g = this.scene.add.graphics();
      // Eight legs (drawn before body so body covers their roots)
      g.lineStyle(2, 0x0a0a0a, 1);
      const r = TILE_SIZE * 0.34;
      const r2 = TILE_SIZE * 0.5;
      const legOffsets = [-10, -3, 3, 10];
      for (const ly of legOffsets) {
        g.lineBetween(-r * 0.7, ly, -r2, ly + Math.sign(ly) * 4);
        g.lineBetween(r * 0.7, ly, r2, ly + Math.sign(ly) * 4);
      }
      g.fillStyle(0x111111, 1);
      g.fillCircle(0, 0, r);
      g.fillStyle(0x303030, 0.6);
      g.fillCircle(-4, -6, r * 0.35);
      g.fillStyle(0xc04040, 1);
      g.fillCircle(-6, -4, 2.4);
      g.fillCircle(6, -4, 2.4);
      child = g;
    }
    const c = this.scene.add.container(0, 0, [child]);
    c.setDepth(40);
    return { container: c, sprite };
  }

  /**
   * Flip the spider sprite horizontally based on patrol direction.
   *
   * Only applies to horizontal patrols (left/right makes visual sense).
   * Vertical patrols leave the sprite in its source orientation since
   * facing up vs down doesn't usually map to a horizontal flip and we'd
   * need separate frames to do it well.
   */
  private applyFacing(s: SpiderInstance): void {
    if (!s.sprite) return;
    if (s.def.axis !== 'horizontal') return;
    // dir === 1 walks toward +x (right, no flip); dir === -1 walks toward -x (left, flip).
    s.sprite.setFlipX(s.dir === -1);
  }

  private teardown(): void {
    for (const s of this.spiders) {
      try {
        if (s.tween && s.tween.isPlaying()) s.tween.stop();
      } catch {
        // tween manager already torn down
      }
      try {
        s.container.destroy();
      } catch {
        // container already destroyed by scene shutdown
      }
    }
    this.spiders = [];
  }
}
